const TOKEN_STORAGE_KEY = 'google-token';
const EXPIRES_AT_STORAGE_KEY = 'token-expires-at';
const SCOPES = 'https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive.readonly';

const DISCOVERY_DOCS = [
  'https://sheets.googleapis.com/$discovery/rest?version=v4',
  'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
];

const script = (url: string) => {
  const scriptElement = document.createElement('script');
  scriptElement.async = true;
  scriptElement.src = url;
  return scriptElement;
};

const injectScript = async (tag: HTMLScriptElement) => {
  if (document.querySelector(`script[src="${tag.src}"]`)) {
    return Promise.resolve(null);
  }
  return new Promise<null>(resolve => {
    document.querySelector('head')?.appendChild(tag);
    tag.onload = () => resolve(null);
  });
};

const onGapiClientLoaded = () => {
  const apiKey = process.env.GOOGLE_API_KEY;
  const clientId = process.env.GOOGLE_OAUTH_CLIENT_ID;
  gapi.client.init({ apiKey, discoveryDocs: DISCOVERY_DOCS, clientId });
  const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    const decoded = JSON.parse(token);
    gapi.client.setToken(decoded);
  }
};

const injectGapi = async () => {
  const googleApi = script('https://apis.google.com/js/api.js');
  return injectScript(googleApi)
    .then(() => gapi.load('client', () => null))
    .then(() => gapi.load('picker', onGapiClientLoaded));
};

const injectGis = async () => {
  const googleIdService = script('https://accounts.google.com/gsi/client');
  return injectScript(googleIdService);
};

const loadGapi = async () => (window.gapi ? Promise.resolve(null) : injectGapi());
const loadGis = async () => (window.google?.accounts ? Promise.resolve(null) : injectGis());

const onTokenReceived = (resolver: (client: typeof gapi) => void) => () => {
  const token = gapi.client.getToken();
  sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(token));

  const expiresAtTime = new Date().getTime() + parseInt(token.expires_in, 10) * 1000;
  sessionStorage.setItem(EXPIRES_AT_STORAGE_KEY, new Date(expiresAtTime).toISOString());

  resolver(gapi);
};

const validToken = (): boolean => {
  const expiresAtString = sessionStorage.getItem(EXPIRES_AT_STORAGE_KEY);
  if (!expiresAtString || !gapi?.client?.getToken()) {
    return false;
  }

  const expiresAt = new Date(expiresAtString);
  const now = new Date();
  return expiresAt.getTime() >= now.getTime() + 30 * 1000;
};

const tokenClient = (): Promise<typeof gapi> =>
  init().then(
    () =>
      new Promise(resolve => {
        const clientId = process.env.GOOGLE_OAUTH_CLIENT_ID;
        const conf = { client_id: clientId, scope: SCOPES, callback: onTokenReceived(resolve) };
        const tokenClient = google.accounts.oauth2.initTokenClient(conf);
        tokenClient.requestAccessToken({ prompt: 'consent' });
      }),
  );

export const init = async () => Promise.all([loadGis(), loadGapi()]);
export const requestGoogleAccess = async () => (validToken() ? gapi : tokenClient());
